/**
 * 规则引擎 - 核心验证逻辑
 */

import { v4 as uuidv4 } from 'uuid';
import type { 
  FigmaFile, 
  FigmaNode, 
  Rule, 
  RuleContext, 
  RuleResult, 
  ValidationResult, 
  RuleEngineConfig, 
  ComponentTree, 
  DesignToken 
} from '../types';
import { Logger } from '../utils/logger';
import { ruleRegistry } from './rule-registry';

/**
 * 规则引擎类
 */
export class RuleEngine {
  private config: RuleEngineConfig;
  private logger: Logger;

  constructor(config: Partial<RuleEngineConfig> = {}) {
    this.config = {
      rules: {},
      globalOptions: {
        parallel: true,
        maxConcurrency: 10,
        logLevel: 'info',
        ...config.globalOptions,
      },
      ...config,
    };

    this.logger = new Logger({
      level: this.config.globalOptions.logLevel,
      traceId: this.config.globalOptions.traceId,
    });
  }

  /**
   * 验证整个 Figma 文件
   */
  async validateFile(figmaFile: FigmaFile): Promise<ValidationResult[]> {
    const startTime = Date.now();
    const traceId = this.config.globalOptions.traceId || uuidv4();
    
    this.logger.setTraceId(traceId);
    
    // 获取所有节点
    const allNodes = this.flattenNodes(figmaFile.nodes);
    const enabledRules = ruleRegistry.getEnabled();
    
    this.logger.engineStart(allNodes.length, enabledRules.length);
    
    try {
      let results: ValidationResult[];
      
      if (this.config.globalOptions.parallel) {
        results = await this.validateNodesParallel(figmaFile, allNodes, enabledRules);
      } else {
        results = await this.validateNodesSequential(figmaFile, allNodes, enabledRules);
      }
      
      const duration = Date.now() - startTime;
      this.logger.engineEnd(allNodes.length, results.length, duration);
      
      return results;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.error('规则引擎执行失败', {
        error: error instanceof Error ? error.message : String(error),
        duration,
        nodeCount: allNodes.length,
        ruleCount: enabledRules.length,
      });
      throw error;
    }
  }

  /**
   * 验证单个节点
   */
  async validateNode(
    figmaFile: FigmaFile, 
    node: FigmaNode, 
    rules?: Rule[]
  ): Promise<ValidationResult> {
    const traceId = this.config.globalOptions.traceId || uuidv4();
    this.logger.setTraceId(traceId);
    
    const rulesToApply = rules || ruleRegistry.getEnabled();
    const context = this.createRuleContext(figmaFile, node, traceId);
    
    const ruleResults = await this.applyRulesToNode(context, rulesToApply);
    
    return {
      traceId,
      nodeId: node.id,
      nodeName: node.name,
      rules: ruleResults,
      timestamp: new Date(),
    };
  }

  /**
   * 并行验证节点
   */
  private async validateNodesParallel(
    figmaFile: FigmaFile,
    nodes: FigmaNode[],
    rules: Rule[]
  ): Promise<ValidationResult[]> {
    const concurrency = this.config.globalOptions.maxConcurrency || 10;
    const results: ValidationResult[] = [];
    
    // 分批处理节点
    for (let i = 0; i < nodes.length; i += concurrency) {
      const batch = nodes.slice(i, i + concurrency);
      const batchPromises = batch.map(node => this.validateNode(figmaFile, node, rules));
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      this.logger.debug(`完成批次验证`, {
        batchStart: i,
        batchSize: batch.length,
        totalProgress: `${Math.min(i + concurrency, nodes.length)}/${nodes.length}`,
      });
    }
    
    return results;
  }

  /**
   * 串行验证节点
   */
  private async validateNodesSequential(
    figmaFile: FigmaFile,
    nodes: FigmaNode[],
    rules: Rule[]
  ): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];
    
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const result = await this.validateNode(figmaFile, node, rules);
      results.push(result);
      
      if ((i + 1) % 10 === 0) {
        this.logger.debug(`验证进度`, {
          progress: `${i + 1}/${nodes.length}`,
          percentage: Math.round(((i + 1) / nodes.length) * 100),
        });
      }
    }
    
    return results;
  }

  /**
   * 对节点应用规则
   */
  private async applyRulesToNode(
    context: RuleContext,
    rules: Rule[]
  ): Promise<Array<{ ruleName: string; result: RuleResult }>> {
    const results: Array<{ ruleName: string; result: RuleResult }> = [];
    
    for (const rule of rules) {
      // 检查规则是否应该应用于此节点
      if (rule.shouldApply && !rule.shouldApply(context)) {
        continue;
      }
      
      const startTime = Date.now();
      this.logger.ruleStart(rule.name, context.node.id, context.node.name);
      
      try {
        const result = await rule.validate(context);
        const duration = Date.now() - startTime;
        
        this.logger.ruleEnd(rule.name, context.node.id, result.valid, duration);
        
        results.push({
          ruleName: rule.name,
          result,
        });
      } catch (error) {
        const duration = Date.now() - startTime;
        this.logger.error(`规则执行失败: ${rule.name}`, {
          nodeId: context.node.id,
          nodeName: context.node.name,
          error: error instanceof Error ? error.message : String(error),
          duration,
        });
        
        // 创建错误结果
        results.push({
          ruleName: rule.name,
          result: {
            valid: false,
            message: `规则执行失败: ${error instanceof Error ? error.message : String(error)}`,
            severity: 'error',
          },
        });
      }
    }
    
    return results;
  }

  /**
   * 创建规则上下文
   */
  private createRuleContext(
    figmaFile: FigmaFile,
    node: FigmaNode,
    traceId: string
  ): RuleContext {
    const parent = this.findParentNode(figmaFile.nodes, node.id);
    const siblings = this.findSiblingNodes(figmaFile.nodes, node.id);
    const componentTree = this.buildComponentTree(node);
    const designTokens = this.extractDesignTokens(figmaFile);
    
    return {
      traceId,
      node,
      parent,
      siblings,
      componentTree,
      designTokens,
      figmaFile,
    };
  }

  /**
   * 扁平化节点树
   */
  private flattenNodes(nodes: FigmaNode[]): FigmaNode[] {
    const result: FigmaNode[] = [];
    
    const traverse = (nodeList: FigmaNode[]) => {
      for (const node of nodeList) {
        result.push(node);
        if (node.children) {
          traverse(node.children);
        }
      }
    };
    
    traverse(nodes);
    return result;
  }

  /**
   * 查找父节点
   */
  private findParentNode(nodes: FigmaNode[], targetId: string): FigmaNode | undefined {
    const traverse = (nodeList: FigmaNode[]): FigmaNode | undefined => {
      for (const node of nodeList) {
        if (node.children?.some(child => child.id === targetId)) {
          return node;
        }
        if (node.children) {
          const found = traverse(node.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    
    return traverse(nodes);
  }

  /**
   * 查找兄弟节点
   */
  private findSiblingNodes(nodes: FigmaNode[], targetId: string): FigmaNode[] {
    const parent = this.findParentNode(nodes, targetId);
    if (!parent || !parent.children) {
      return [];
    }
    
    return parent.children.filter(child => child.id !== targetId);
  }

  /**
   * 构建组件树
   */
  private buildComponentTree(node: FigmaNode, depth = 0, path: string[] = []): ComponentTree {
    const currentPath = [...path, node.name];
    
    return {
      node,
      children: (node.children || []).map(child => 
        this.buildComponentTree(child, depth + 1, currentPath)
      ),
      depth,
      path: currentPath,
    };
  }

  /**
   * 提取设计令牌
   */
  private extractDesignTokens(_figmaFile: FigmaFile): DesignToken[] {
    // 这里可以实现从 Figma 文件中提取设计令牌的逻辑
    // 目前返回空数组，后续可以扩展
    return [];
  }

  /**
   * 获取当前 traceId
   */
  getTraceId(): string {
    return this.logger.getTraceId();
  }

  /**
   * 设置新的 traceId
   */
  setTraceId(traceId: string): void {
    this.logger.setTraceId(traceId);
    this.config.globalOptions.traceId = traceId;
  }

  /**
   * 获取引擎统计信息
   */
  getStats(): {
    enabledRules: number;
    totalRules: number;
    config: RuleEngineConfig;
  } {
    return {
      enabledRules: ruleRegistry.getEnabled().length,
      totalRules: ruleRegistry.size(),
      config: this.config,
    };
  }
}

// 便捷函数
export function createRuleEngine(config?: Partial<RuleEngineConfig>): RuleEngine {
  return new RuleEngine(config);
}